<script lang="ts" setup>
  import type { PropType } from 'vue'
  import {
    EntitiesMapperKeyEnum,
    type TDataMapper,
    type ProposedEntityChange,
    ProposedEntityChangeStatusEnum
  } from '@/types/generated'
  import { FieldTypeEnum } from '@/types/generated'
  import { computed, ref, watch } from 'vue'
  import { yup } from '@/lib/validation'
  import ComboField from '@/components/admin/form/ComboField.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import { CheckIcon, CopyIcon, X } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import { useTranslation } from '@/composables/useTranslation'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

  const props = defineProps({
    value: {
      // Using type: null tells Vue to accept any type for this prop
      // This is the Vue-specific way to allow any value type
      type: null,
      required: true
    },
    mapper: {
      type: Object as PropType<TDataMapper>,
      required: true
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    customValidator: {
      type: Object as PropType<yup.AnySchema | null>,
      required: false,
      default: () => null
    },
    proposedChange: {
      type: Object as PropType<ProposedEntityChange | null>,
      required: false,
      default: () => null
    }
  })

  const proposedValue = computed<string | null>(() => {
    if (!props.proposedChange?.proposed_value) return null

    const rawValue = props.proposedChange.proposed_value as { value: any }
    return rawValue.value
  })

  const emit = defineEmits([
    'onValueChange',
    'valueChangeError',
    'acceptProposedChange',
    'rejectProposedChange'
  ])

  const { t } = useTranslation({
    en: {
      showMore: 'Show more',
      fullText: 'Full Text',
      accept: 'Accept',
      reject: 'Reject',
      copy: 'Copy',
      copied: 'Copied!'
    },
    de: {
      showMore: 'Mehr anzeigen',
      fullText: 'Vollständiger Text',
      accept: 'Bestätigen',
      reject: 'Ablehnen',
      copy: 'Kopieren',
      copied: 'Kopiert!'
    }
  })

  const localValue = ref(props.value)

  const rawValue = ref(props.value)

  const errorMessage = ref('')

  // Update local value when prop changes
  watch(
    () => props.value,
    (newValue) => {
      localValue.value = newValue
    }
  )

  const isPopoverOpen = ref(false)
  // Computed property to check if the text is long enough to show the "Show more" button
  const isLongText = computed(() => {
    const currentValue = modelValue.value || props.value
    return typeof currentValue === 'string' && currentValue.length > 150
  })

  const validationRules = computed(() => {
    if (!props.mapper.field_type) return null

    const schema = (() => {
      if (props.customValidator) {
        return props.customValidator
      }

      switch (props.mapper.field_type) {
        case FieldTypeEnum.NUMBERINPUT:
          return yup.number().integer().nullable()
        case FieldTypeEnum.DECIMALNUMBERINPUT:
          return yup.number().nullable()
        case FieldTypeEnum.TEXTINPUT:
        case FieldTypeEnum.TEXTAREA:
          return yup.string().nullable()
        case FieldTypeEnum.EMAILINPUT:
          return yup.string().email().nullable()
        case FieldTypeEnum.PHONEINPUT:
          return yup.string().phone().nullable()
        case FieldTypeEnum.URLINPUT:
          return yup.string().url().nullable()
        case FieldTypeEnum.SELECT:
          return yup
            .string()
            .oneOf(props.mapper.choices || [], 'Please select a valid option')
            .nullable()
        case FieldTypeEnum.DATEFIELD:
          return yup
            .string()
            .nullable()
            .matches(
              /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[0-2])\.(19|20)\d{2}$/,
              'Bitte geben Sie ein gültiges Datum im Format TT.MM.JJJJ ein'
            )
            .test('is-valid-date', 'Bitte geben Sie ein existierendes Datum ein', (value) => {
              if (!value) return true // allow empty if nullable
              const [day, month, year] = value.split('.').map(Number)
              const date = new Date(year, month - 1, day)
              return (
                date.getFullYear() === year &&
                date.getMonth() === month - 1 &&
                date.getDate() === day
              )
            })
        case FieldTypeEnum.TIMEFIELD:
          return yup
            .string()
            .nullable()
            .matches(
              /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/,
              'Bitte geben Sie eine gültige Uhrzeit im Format HH:MM ein'
            )
        case FieldTypeEnum.YESNO:
          return yup.boolean()
        default:
          return null
      }
    })()

    return schema
  })

  const validateValue = (value: any) => {
    if (!validationRules.value) return
    validationRules.value
      .validate(value)
      .then(() => {
        emit('valueChangeError', props.mapper.key, false)
        errorMessage.value = ''
      })
      .catch((error: Error) => {
        emit('valueChangeError', props.mapper.key, true)
        errorMessage.value = error.message
      })
  }

  // Watch for changes in validation rules and re-validate current value
  watch(
    validationRules,
    () => {
      if (localValue.value !== undefined && localValue.value !== null && localValue.value !== '') {
        validateValue(localValue.value)
      }
    },
    { deep: true }
  )

  const modelValue = computed({
    get: () => {
      // When getting values, ensure numbers are properly formatted for display
      if (
        props.mapper.field_type === FieldTypeEnum.NUMBERINPUT ||
        props.mapper.field_type === FieldTypeEnum.DECIMALNUMBERINPUT
      ) {
        // If the value is a string that represents a number, parse it for proper display
        if (typeof localValue.value === 'string' && !isNaN(Number(localValue.value))) {
          return Number(localValue.value)
        }
      }

      // validate raw value
      validateValue(rawValue.value)

      return localValue.value
    },
    set: async (newValue) => {
      // always save the raw value to verify it with the local value
      rawValue.value = newValue

      // start processing the new value
      let normalizedNewValue = newValue

      // Normalize numeric values to ensure consistent type handling
      if (
        props.mapper.field_type === FieldTypeEnum.NUMBERINPUT ||
        props.mapper.field_type === FieldTypeEnum.DECIMALNUMBERINPUT
      ) {
        // If the value is a number, convert it to string to ensure consistent storage
        if (typeof normalizedNewValue === 'number') {
          normalizedNewValue = String(normalizedNewValue)
        } else if (typeof normalizedNewValue === 'string' && normalizedNewValue !== '') {
          // Ensure string numbers are properly formatted (no extra spaces, etc.)
          const parsedNum = Number(normalizedNewValue)
          if (!isNaN(parsedNum)) {
            normalizedNewValue = String(parsedNum)
          }
        }
      }

      if (localValue.value === normalizedNewValue) {
        return
      }

      if (!validationRules.value) {
        localValue.value = normalizedNewValue
        emit('onValueChange', normalizedNewValue)
        emit('valueChangeError', props.mapper.key, false)
        errorMessage.value = ''
        return
      }

      try {
        await validationRules.value.validate(normalizedNewValue)
        localValue.value = normalizedNewValue
        emit('onValueChange', normalizedNewValue)
        emit('valueChangeError', props.mapper.key, false)
        errorMessage.value = ''
      } catch (error) {
        // Value is invalid, don't emit the change
        // but, emit error presence instead
        emit('valueChangeError', props.mapper.key, true)
        errorMessage.value = (error as Error).message
      }
    }
  })

  // Predefined options for specific title combo field
  const specificTitleOptions = [
    { value: 'Prof.', title: 'Prof.' },
    { value: 'Prof. Dr.', title: 'Prof. Dr.' },
    { value: 'Dr.', title: 'Dr.' },
    { value: 'Dr. Dr.', title: 'Dr. Dr.' }
  ]

  const renderComboField = computed<Boolean>(() => {
    // Render combo field for specific_title
    return props.mapper.key === EntitiesMapperKeyEnum.ENTITIES_FIELD_SPECIFIC_TITLE
  })

  const renderInputField = computed<Boolean>(() => {
    // if the mapper field type is in textinput, numberinput, emailinput etc
    // but not if it's a combo field (specific_title)
    if (renderComboField.value) return false
    return [
      FieldTypeEnum.TEXTINPUT,
      FieldTypeEnum.NUMBERINPUT,
      FieldTypeEnum.DECIMALNUMBERINPUT,
      FieldTypeEnum.EMAILINPUT,
      FieldTypeEnum.PHONEINPUT,
      FieldTypeEnum.URLINPUT,
      FieldTypeEnum.DATEFIELD,
      FieldTypeEnum.TIMEFIELD
    ].includes(props.mapper.field_type)
  })

  const renderCheckboxField = computed<Boolean>(() => {
    return props.mapper.field_type == FieldTypeEnum.YESNO
  })

  const renderSelectField = computed<Boolean>(() => {
    return props.mapper.field_type == FieldTypeEnum.SELECT
  })

  const renderTextareaField = computed<Boolean>(() => {
    return props.mapper.field_type == FieldTypeEnum.TEXTAREA
  })

  const selectFieldOptions = computed(() => {
    // hardcoding the title of entity_types temporarily
    // TODO: find a solution for that later
    if (props.mapper.key === EntitiesMapperKeyEnum.ENTITIES_FIELD_ENTITY_TYPE) {
      return [
        {
          title: 'Natürliche Person',
          value: 'natural_person'
        },
        {
          title: 'Juristiche Person',
          value: 'legal_person'
        },
        {
          title: 'Institution',
          value: 'institution'
        }
      ]
    }
    return props.mapper.choices?.map((option) => {
      return {
        title: option === '' ? '---' : option,
        value: option
      }
    })
  })

  const isCopied = ref(false)
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(String(localValue.value || ''))
      isCopied.value = true
      setTimeout(() => {
        isCopied.value = false
      }, 2000)
    } catch (err) {
      console.error('Failed to copy text: ', err)
    }
  }

  const isProcessingChange = ref(false)

  const acceptProposedChange = () => {
    if (props.proposedChange && !isProcessingChange.value) {
      isProcessingChange.value = true
      emit('acceptProposedChange', props.proposedChange)
    }
  }

  const discardProposedChange = () => {
    if (props.proposedChange && !isProcessingChange.value) {
      isProcessingChange.value = true
      emit('rejectProposedChange', props.proposedChange)
    }
  }
</script>

<template>
  <div
    v-if="mapper"
    class="flex w-full items-center"
  >
    <!-- Proposed Change Display -->
    <div
      v-if="proposedValue && proposedChange?.status === ProposedEntityChangeStatusEnum.PENDING"
      class="flex w-full items-center gap-1.5 rounded-lg border border-dashed border-purple-600 bg-purple-50
        px-2.5 py-2 text-sm"
    >
      <div class="flex-1 cursor-default truncate">
        <div class="truncate text-gray-500 line-through">{{ value }}</div>
        <div class="truncate text-gray-800">{{ proposedValue }}</div>
      </div>
      <div class="flex items-center gap-1">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger as-child>
              <Button
                variant="secondary"
                size="icon"
                class="h-6 w-6 border border-purple-200 text-purple-600 hover:border-purple-300 hover:bg-gray-100
                  hover:text-purple-600"
                :disabled="isProcessingChange"
                @click="discardProposedChange"
              >
                <X class="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              variant="secondary"
              show-arrow
            >
              <p>{{ t('reject') }}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger as-child>
              <Button
                variant="secondary"
                size="icon"
                class="h-6 w-6 border border-purple-200 text-purple-600 hover:border-purple-300 hover:bg-gray-100
                  hover:text-purple-600"
                :disabled="isProcessingChange"
                @click="acceptProposedChange"
              >
                <CheckIcon class="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              variant="secondary"
              show-arrow
            >
              <p>{{ t('accept') }}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>

    <!-- Regular Input Fields (shown when no proposed change) -->
    <div
      v-else
      class="w-full"
    >
      <ComboField
        v-if="renderComboField"
        v-model="modelValue"
        :name="mapper.key"
        :options="specificTitleOptions"
        :disabled="disabled"
        :error="errorMessage"
        data-cy="fieldValueInput"
      />
      <InputField
        v-else-if="renderInputField"
        v-model="modelValue"
        :type="
          mapper.field_type == FieldTypeEnum.NUMBERINPUT ||
          mapper.field_type == FieldTypeEnum.DECIMALNUMBERINPUT
            ? 'number'
            : mapper.field_type == FieldTypeEnum.TIMEFIELD
              ? 'time'
              : 'text'
        "
        :name="mapper.key"
        :rules="validationRules"
        :step="mapper.field_type == FieldTypeEnum.DECIMALNUMBERINPUT ? '0.01' : '1'"
        :disabled="disabled"
        :error="errorMessage"
        data-cy="fieldValueInput"
      />
      <SelectField
        v-else-if="renderSelectField"
        v-model="modelValue"
        :options="selectFieldOptions"
        :name="mapper.key"
        :rules="validationRules"
        :disabled="disabled"
        :error="errorMessage"
      />
      <CheckboxField
        v-else-if="renderCheckboxField"
        v-model="modelValue"
        :name="mapper.key"
        :disabled="disabled"
        :error="errorMessage"
      />
      <TextAreaField
        v-else-if="renderTextareaField"
        v-model="modelValue"
        :name="mapper.key"
        :rules="validationRules"
        :disabled="disabled"
        :error="errorMessage"
      />

      <div v-else>
        field: {{ mapper.field_type }}
        <br />
        rules: {{ validationRules }}
        <span class="strong">NOT YET IMPLEMENTED</span>
      </div>
      <Popover
        v-if="isLongText"
        :open="isPopoverOpen"
        @update:open="isPopoverOpen = $event"
      >
        <PopoverTrigger class="inline-block w-full text-end">
          <span class="cursor-pointer text-primary underline">{{ t('showMore') }}</span>
        </PopoverTrigger>
        <PopoverContent
          side="right"
          class="max-h-[70vh] w-[30vw] max-w-[90vw] overflow-auto p-4"
        >
          <div class="mb-2 flex items-center justify-between">
            <h3 class="font-medium">{{ t('fullText') }}</h3>
            <Button
              variant="ghost"
              size="icon"
              class="h-6 w-6"
              @click="isPopoverOpen = false"
            >
              <X class="h-4 w-4" />
            </Button>
          </div>
          <p class="whitespace-pre-wrap break-words">{{ modelValue || value }}</p>
        </PopoverContent>
      </Popover>
    </div>
    <div
      v-if="!proposedChange"
      class="flex items-center"
    >
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger as-child>
            <Button
              variant="ghost"
              size="icon"
              @click="copyToClipboard"
            >
              <CopyIcon
                v-if="!isCopied"
                class="h-4 w-4"
              />
              <span
                v-else
                class="text-xs"
              >
                <CheckIcon class="h-4 w-4 text-green-500" />
              </span>
            </Button>
          </TooltipTrigger>
          <TooltipContent
            variant="secondary"
            show-arrow
          >
            <p>{{ isCopied ? t('copied') : t('copy') }}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
    <!--    <pre>-->
    <!--      {{ mapper }}-->
    <!--    </pre>-->
  </div>
</template>
